import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { NS } from '../U3207';
import { useU3207Context } from '../U3207Context';
import { useU3207DashboardHandlers } from '../hooks/useU3207DashboardHandlers';
import type { U3207DashboardQuery } from '../U3207Service';
import {
  u3207TruncateTrail,
  u3207ResolveDrillFilters,
  type U3207BreadcrumbItem,
} from '../utils/u3207Breadcrumb';
import {
  u3207FormatTokenCount,
  u3207FormatUserLabel,
  u3207FormatQuotaUsage,
  u3207FormatCodeNameLabel,
} from '../utils/u3207Formatters';
import U3207MonthFilter from './U3207MonthFilter';
import U3207CategoryTile, { type U3207CategoryTableRow } from './U3207CategoryTile';
import U3207DrillPanel from './U3207DrillPanel';
import U2000ActionBtn from '../../u2000Core/components/buttons/U2000ActionBtn';

function U3207DashboardScreen() {
  const { t, i18n } = useTranslation(NS);
  const locale = i18n.language;
  const {
    initLU,
    month,
    setMonth,
    providerId,
    setProviderId,
    groupId,
    setGroupId,
    drillTrail,
    setDrillTrail,
    drillOpen,
    setDrillOpen,
  } = useU3207Context();

  const {
    summary,
    summaryLoading,
    summaryError,
    loadSummary,
    drillLoading,
    providerDrill,
    groupDrill,
    userDrill,
    conversationsDrill,
    loadProviderDrill,
    loadGroupDrill,
    loadUserDrill,
    loadConversationsDrill,
    clearDrill,
  } = useU3207DashboardHandlers();

  const baseQuery = useCallback((): U3207DashboardQuery => {
    const q: U3207DashboardQuery = { month };
    if (providerId) q.providerId = providerId;
    if (groupId) q.groupId = groupId;
    return q;
  }, [month, providerId, groupId]);

  /** Filtres summary + contexte du fil d'Ariane (provider/groupe du parcours). */
  const drillQuery = useCallback(
    (trail: U3207BreadcrumbItem[]): U3207DashboardQuery => {
      const filters = u3207ResolveDrillFilters(trail, {
        providerId: providerId || undefined,
        groupId: groupId || undefined,
      });
      return {
        month,
        ...filters,
      };
    },
    [month, providerId, groupId],
  );

  useEffect(() => {
    void loadSummary(baseQuery());
  }, [baseQuery, loadSummary]);

  const openTrail = useCallback(
    (trail: U3207BreadcrumbItem[]) => {
      setDrillTrail(trail);
      setDrillOpen(true);
    },
    [setDrillOpen, setDrillTrail],
  );

  const closeDrill = useCallback(() => {
    setDrillOpen(false);
    setDrillTrail([]);
    clearDrill();
  }, [clearDrill, setDrillOpen, setDrillTrail]);

  const reloadTrailTip = useCallback(
    async (trail: U3207BreadcrumbItem[]) => {
      const tip = trail[trail.length - 1];
      if (!tip) {
        clearDrill();
        return;
      }
      const q = drillQuery(trail);
      if (tip.level === 'provider' && tip.id) {
        await loadProviderDrill(tip.id, q);
      } else if (tip.level === 'group' && tip.id) {
        await loadGroupDrill(tip.id, q);
      } else if (tip.level === 'groupUsers' && tip.id) {
        await loadGroupDrill(tip.id, { ...q, level: 'users' });
      } else if (tip.level === 'user' && tip.id) {
        await loadUserDrill(tip.id, q);
      } else if (tip.level === 'conversations') {
        await loadConversationsDrill(q);
      } else if (tip.level === 'userConversations' && tip.id) {
        await loadConversationsDrill({ ...q, userId: tip.id });
      }
    },
    [
      clearDrill,
      drillQuery,
      loadConversationsDrill,
      loadGroupDrill,
      loadProviderDrill,
      loadUserDrill,
    ],
  );

  useEffect(() => {
    if (!drillOpen) return;
    void reloadTrailTip(drillTrail);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- volontairement limité à month/providerId/groupId
  }, [month, providerId, groupId]);

  const onBreadcrumbClick = useCallback(
    async (trailIndex: number) => {
      const next = u3207TruncateTrail(drillTrail, trailIndex);
      setDrillTrail(next);
      if (!next.length) {
        closeDrill();
        return;
      }
      await reloadTrailTip(next);
    },
    [closeDrill, drillTrail, reloadTrailTip, setDrillTrail],
  );

  const openProvider = async (providerIdValue: string, label: string) => {
    const trail: U3207BreadcrumbItem[] = [{ level: 'provider', id: providerIdValue, label }];
    openTrail(trail);
    await loadProviderDrill(providerIdValue, drillQuery(trail));
  };

  const openGroup = async (groupIdValue: string, label: string) => {
    const trail: U3207BreadcrumbItem[] = [{ level: 'group', id: groupIdValue, label }];
    openTrail(trail);
    await loadGroupDrill(groupIdValue, drillQuery(trail));
  };

  const openConversations = async () => {
    const trail: U3207BreadcrumbItem[] = [{ level: 'conversations', label: t('tile.conversations') }];
    openTrail(trail);
    await loadConversationsDrill(drillQuery(trail));
  };

  const openUser = async (userIdValue: string, label: string) => {
    const trail: U3207BreadcrumbItem[] = [{ level: 'user', id: userIdValue, label }];
    openTrail(trail);
    await loadUserDrill(userIdValue, drillQuery(trail));
  };

  const onSelectGroupFromProvider = async (
    groupIdValue: string,
    label: string,
    parentProviderId?: string,
  ) => {
    const next: U3207BreadcrumbItem[] = [
      ...drillTrail,
      { level: 'group', id: groupIdValue, label, parentId: parentProviderId },
    ];
    setDrillTrail(next);
    await loadGroupDrill(groupIdValue, drillQuery(next));
  };

  const onSelectGroupUsers = async (groupIdValue: string, label: string) => {
    const withoutUsers = drillTrail.filter((item) => item.level !== 'groupUsers');
    const hasGroup = withoutUsers.some((item) => item.level === 'group' && item.id === groupIdValue);
    const base = hasGroup
      ? withoutUsers
      : [...withoutUsers, { level: 'group' as const, id: groupIdValue, label }];
    const next: U3207BreadcrumbItem[] = [
      ...base,
      { level: 'groupUsers', id: groupIdValue, label: t('kpi.users') },
    ];
    setDrillTrail(next);
    const q = { ...drillQuery(next), level: 'users' as const };
    const groupResult = await loadGroupDrill(groupIdValue, q);
    // Un seul usager : ouvrir directement son détail (breadcrumb se termine par son nom).
    if (groupResult?.users?.length === 1) {
      const only = groupResult.users[0];
      const userLabel = u3207FormatUserLabel(only);
      const withUser: U3207BreadcrumbItem[] = [
        ...next,
        { level: 'user', id: only.userId, label: userLabel },
      ];
      setDrillTrail(withUser);
      await loadUserDrill(only.userId, drillQuery(withUser));
    }
  };

  const onSelectUser = async (userIdValue: string, label: string) => {
    const withoutUserLevels = drillTrail.filter(
      (item) => item.level !== 'user' && item.level !== 'userConversations',
    );
    const next: U3207BreadcrumbItem[] = [
      ...withoutUserLevels,
      { level: 'user', id: userIdValue, label },
    ];
    setDrillTrail(next);
    await loadUserDrill(userIdValue, drillQuery(next));
  };

  const onSelectUserConversations = async (userIdValue: string, _label: string) => {
    const next: U3207BreadcrumbItem[] = [
      ...drillTrail,
      { level: 'userConversations', id: userIdValue, label: t('tile.conversations') },
    ];
    setDrillTrail(next);
    await loadConversationsDrill({ ...drillQuery(next), userId: userIdValue });
  };

  const filteredUsers = (summary?.users ?? []).filter(
    (u) => !groupId || u.groupId === groupId,
  );

  const providerRows: U3207CategoryTableRow[] =
    summary?.providers
      .filter((p) => !providerId || p.providerId === providerId)
      .map((p) => {
        const label = u3207FormatCodeNameLabel(p.code, p.name);
        return {
          id: p.providerId,
          name: label,
          tokens: p.tokens,
          extraCells: [
            p.distinctUsers,
            p.distinctConversations,
            u3207FormatQuotaUsage({
              usedTokens: p.tokens,
              quotaMonthlyTokens: p.orgQuotaMonthlyTokens,
              usagePercent: p.orgUsagePercent,
              locale,
              unlimitedLabel: t('kpi.unlimited'),
            }),
          ],
          onClick: () => void openProvider(p.providerId, label),
        };
      }) ?? [];

  const groupRows: U3207CategoryTableRow[] =
    summary?.groups
      .filter((g) => !groupId || g.groupId === groupId)
      .flatMap((g) => {
        const groupLabel = u3207FormatCodeNameLabel(g.code, g.name);
        const providers = g.providers.filter(
          (p) => !providerId || p.providerId === providerId,
        );
        if (!providers.length) {
          if (providerId) return [];
          return [
            {
              id: g.groupId,
              name: groupLabel,
              tokens: 0,
              extraCells: ['—', t('kpi.unlimited'), g.conversationCount],
              onClick: () => void openGroup(g.groupId, groupLabel),
            },
          ];
        }
        return providers.map((p) => ({
          id: `${g.groupId}-${p.providerId}`,
          name: groupLabel,
          tokens: p.tokens,
          extraCells: [
            u3207FormatCodeNameLabel(p.code, p.name),
            u3207FormatQuotaUsage({
              usedTokens: p.tokens,
              quotaMonthlyTokens: p.groupQuotaMonthlyTokens,
              usagePercent: p.usagePercent,
              locale,
              unlimitedLabel: t('kpi.unlimited'),
            }),
            g.conversationCount,
          ],
          onClick: () => void openGroup(g.groupId, groupLabel),
        }));
      }) ?? [];

  const conversationRows: U3207CategoryTableRow[] = filteredUsers.map((u) => {
    const label = u3207FormatUserLabel(u);
    const tokens = providerId
      ? (u.providers.find((p) => p.providerId === providerId)?.tokens ?? 0)
      : u.totalTokens;
    return {
      id: `conv-${u.userId}`,
      name: label,
      tokens,
      extraCells: [u.conversationCount],
      onClick: () => void openUser(u.userId, label),
    };
  });

  const userRows: U3207CategoryTableRow[] = filteredUsers.flatMap((u) => {
    const label = u3207FormatUserLabel(u);
    const providers = u.providers.filter(
      (p) => !providerId || p.providerId === providerId,
    );
    if (!providers.length) {
      if (providerId) return [];
      return [
        {
          id: u.userId,
          name: label,
          tokens: u.totalTokens,
          extraCells: ['—', t('kpi.unlimited'), '—', u.groupCode ?? '—'],
          onClick: () => void openUser(u.userId, label),
        },
      ];
    }
    return providers.map((p) => ({
      id: `${u.userId}-${p.providerId}`,
      name: label,
      tokens: p.tokens,
      extraCells: [
        `${u3207FormatCodeNameLabel(p.code, p.name)}`,
        u3207FormatQuotaUsage({
          usedTokens: p.tokens,
          quotaMonthlyTokens: p.userQuotaMonthlyTokens,
          usagePercent: p.usagePercent,
          locale,
          unlimitedLabel: t('kpi.unlimited'),
        }),
        p.remainingTokens != null
          ? u3207FormatTokenCount(p.remainingTokens, locale)
          : '—',
        u.groupCode ?? '—',
      ],
      onClick: () => void openUser(u.userId, label),
    }));
  });

  const isEmpty =
    summary &&
    summary.providers.length === 0 &&
    summary.groups.length === 0 &&
    summary.users.length === 0 &&
    summary.conversations.conversationCount === 0;

  return (
    <div className="flex flex-col gap-4 p-4 lg:flex-row">
      <div className="min-w-0 flex-1 space-y-4">
        <U3207MonthFilter
          month={month}
          onMonthChange={setMonth}
          providerId={providerId}
          onProviderChange={setProviderId}
          groupId={groupId}
          onGroupChange={setGroupId}
          providers={initLU.providers}
          groups={initLU.groups}
        />

        {summaryLoading && !summary ? (
          <p className="text-sm text-muted-foreground">{t('loading')}</p>
        ) : null}

        {summaryError && !summary ? (
          <p className="text-sm text-destructive">{t('error.load')}</p>
        ) : null}

        {!summaryLoading && isEmpty ? (
          <p className="rounded-lg border border-dashed border-border p-6 text-sm text-muted-foreground">
            {t('empty')}
          </p>
        ) : null}

        {summary && !isEmpty ? (
          <div className="flex flex-col gap-4">
            <U3207CategoryTile
              title={t('tile.provider')}
              summaryMetrics={[
                {
                  label: t('kpi.tokens'),
                  value: u3207FormatTokenCount(
                    summary.providers.reduce((s, p) => s + p.tokens, 0),
                    locale,
                  ),
                },
                { label: t('table.rows'), value: summary.providers.length },
              ]}
              extraHeaders={[t('kpi.users'), t('kpi.conversations'), t('kpi.quota')]}
              rows={providerRows}
            />

            <U3207CategoryTile
              title={t('tile.group')}
              summaryMetrics={[
                {
                  label: t('kpi.tokens'),
                  value: u3207FormatTokenCount(
                    summary.groups.reduce(
                      (s, g) => s + g.providers.reduce((ps, p) => ps + p.tokens, 0),
                      0,
                    ),
                    locale,
                  ),
                },
                { label: t('table.rows'), value: groupRows.length },
              ]}
              extraHeaders={[t('kpi.provider'), t('kpi.quota'), t('kpi.conversations')]}
              rows={groupRows}
            />

            <U3207CategoryTile
              title={t('tile.conversations')}
              summaryMetrics={[
                { label: t('kpi.activeUsers'), value: summary.conversations.activeUsers },
                {
                  label: t('kpi.conversations'),
                  value: summary.conversations.conversationCount,
                },
              ]}
              extraHeaders={[t('kpi.conversations')]}
              rows={conversationRows}
              headerAction={
                <U2000ActionBtn
                  variant="secondary"
                  className="px-2! py-1! text-xs"
                  label={t('drill.openConversations')}
                  handleClick={() => void openConversations()}
                />
              }
            />

            <U3207CategoryTile
              title={t('tile.user')}
              summaryMetrics={[
                {
                  label: t('kpi.tokens'),
                  value: u3207FormatTokenCount(
                    userRows.reduce((s, r) => s + r.tokens, 0),
                    locale,
                  ),
                },
                { label: t('table.rows'), value: userRows.length },
              ]}
              extraHeaders={[
                t('kpi.provider'),
                t('kpi.quota'),
                t('kpi.remaining'),
                t('kpi.group'),
              ]}
              rows={userRows}
            />
          </div>
        ) : null}
      </div>

      <U3207DrillPanel
        open={drillOpen}
        trail={drillTrail}
        loading={drillLoading}
        providerDrill={providerDrill}
        groupDrill={groupDrill}
        userDrill={userDrill}
        conversationsDrill={conversationsDrill}
        scopedProviderId={u3207ResolveDrillFilters(drillTrail, {
          providerId: providerId || undefined,
        }).providerId}
        onClose={closeDrill}
        onBreadcrumbClick={(idx) => void onBreadcrumbClick(idx)}
        onSelectGroup={(id, label, parentId) => void onSelectGroupFromProvider(id, label, parentId)}
        onSelectGroupUsers={(id, label) => void onSelectGroupUsers(id, label)}
        onSelectUser={(id, label) => void onSelectUser(id, label)}
        onSelectUserConversations={(id, label) => void onSelectUserConversations(id, label)}
      />
    </div>
  );
}

export default U3207DashboardScreen;
